import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, Line, Radar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  RadialLinearScale,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import Lottie from "lottie-react";
import octopusAnimation from "../assets/IQ.json";
import scorecardBaseImage from "../assets/scorecard-base.png";
import mysteryImage from "../assets/mystery.jpg";
import timetableImage from "../assets/timetable.jpg";
import studyImage from "../assets/study.jpg";
import brainImage from "../assets/brain.jpg";
import scenariosImage from "../assets/scenarios.jpg";
import RankList from "./RankList.tsx"; // Adjust path as needed

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  RadialLinearScale,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface DroctoIQDashboardProps {
  userId: string;
}

const DroctoIQDashboard: React.FC<DroctoIQDashboardProps> = ({ userId }) => {
  const [completedChats, setCompletedChats] = useState<number>(0);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [userName, setUserName] = useState<string>("");
  const [createdAt, setCreatedAt] = useState<Date | null>(null);
  const [selectedChallenges, setSelectedChallenges] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<"myScore" | "leaderboard">("myScore");
  const navigate = useNavigate();

  // Fetch user progress and profile data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) return;

      try {
        const token = localStorage.getItem("token");
        const [progressResponse, userResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/auth/user-progress/${userId}`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }),
          fetch(`${process.env.REACT_APP_API_URL}/api/auth/user`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }),
        ]);

        if (!progressResponse.ok) {
          const errorData = await progressResponse.json();
          if (progressResponse.status === 401 && errorData.message === "Token expired, please log in again") {
            localStorage.removeItem("token");
            navigate("/login");
            return;
          }
          if (progressResponse.status === 404) {
            setCompletedChats(0);
            return;
          }
          throw new Error(`Failed to fetch progress data: ${progressResponse.status}`);
        }
        const progressData = await progressResponse.json();
        setCompletedChats(progressData.completedChats || 0);

        if (!userResponse.ok) {
          const errorData = await userResponse.json();
          if (userResponse.status === 401 && errorData.message === "Token expired, please log in again") {
            localStorage.removeItem("token");
            navigate("/login");
            return;
          }
          throw new Error(`Failed to fetch user data: ${userResponse.status}`);
        }
        const userData = await userResponse.json();
        setProfileImage(userData.childImage || null);
        setUserName(userData.fullName || "Unknown");
        setCreatedAt(userData.createdAt ? new Date(userData.createdAt) : new Date());
      } catch (err: any) {
        console.error("Error fetching data:", err.message);
        setCompletedChats(0);
        setProfileImage(null);
        setUserName("Unknown");
        setCreatedAt(new Date());
      }
    };

    fetchUserData();
  }, [userId, navigate]);

  // Handle challenge selection
  const handleChallengeSelect = (title: string) => {
    setSelectedChallenges((prev) =>
      prev.includes(title)
        ? prev.filter((item) => item !== title)
        : [...prev, title]
    );
  };

  // Handle WhatsApp message
  const sendWhatsAppMessage = () => {
    const message = `I'm interested in the following options: ${selectedChallenges.join(", ")}`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/+918822436250?text=${encodedMessage}`;
    window.open(whatsappUrl, "_blank");
  };

  // Handle back navigation for leaderboard
  const handleBack = () => {
    navigate("/profile"); // Adjust route as needed
  };

  // Generate chart labels and data
  const generateChartLabels = (numLabels: number, type: "week" | "month" | "category") => {
    if (!createdAt) {
      return {
        labels: Array(numLabels)
          .fill("")
          .map((_, i) => `Label ${i + 1}`),
        dataSlice: numLabels,
      };
    }

    const today = new Date();
    const timeDiff = today.getTime() - createdAt.getTime();
    const daysDiff = Math.max(Math.floor(timeDiff / (1000 * 60 * 60 * 24)), 35);

    if (type === "week") {
      const totalWeeks = Math.ceil(daysDiff / 7);
      const startWeek = Math.max(1, totalWeeks - numLabels + 1);
      const labels = Array(Math.min(totalWeeks, numLabels))
        .fill("")
        .map((_, i) => `Week ${startWeek + i}`);
      return { labels, dataSlice: numLabels };
    } else if (type === "month") {
      const startDate = new Date(createdAt);
      const endDate = new Date(today);
      let totalMonths = 0;
      let currentDate = new Date(startDate);

      while (currentDate <= endDate) {
        totalMonths++;
        currentDate.setMonth(currentDate.getMonth() + 1);
      }

      totalMonths = Math.max(totalMonths, Math.ceil(daysDiff / 30));
      const startMonthIndex = Math.max(0, totalMonths - numLabels);
      const labels = [];
      currentDate = new Date(startDate);
      currentDate.setMonth(startDate.getMonth() + startMonthIndex);

      for (let i = 0; i < numLabels && currentDate <= endDate; i++) {
        labels.push(currentDate.toLocaleString("default", { month: "short" }));
        currentDate.setMonth(currentDate.getMonth() + 1);
      }

      return { labels, dataSlice: labels.length };
    } else {
      return {
        labels: ["Storytelling", "Ideation", "Originality"].slice(0, numLabels),
        dataSlice: numLabels,
      };
    }
  };

  // Scorecard image configuration
  const scorecardConfig = {
    canvas: { width: 600, height: 800 },
    baseImage: { url: scorecardBaseImage },
    profileImage: {
      x: 462.5,
      y: 516.67,
      radius: 50,
      size: 100,
      borderColor: "#ff6200",
      borderWidth: 2,
      placeholderColor: "#ffffff",
      placeholderBorderColor: "#ff6200",
      placeholderText: "?",
      placeholderFont: "bold 36px Inter",
    },
    iqScore: {
      text: (score: number) => `${score}`,
      font: "bold 48px Helvetica",
      color: "#000000",
      x: 139.5,
      y: 295,
    },
    name: {
      text: (name: string) => name,
      font: "bold 28px Helvetica",
      color: "#000000",
      x: 150,
      y: 496.67,
    },
  };

  // Generate scorecard image
  const generateScorecardImage = async (iqScore: number, name: string): Promise<string> => {
    const canvas = document.createElement("canvas");
    canvas.width = scorecardConfig.canvas.width;
    canvas.height = scorecardConfig.canvas.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";

    try {
      const baseImg = new Image();
      baseImg.crossOrigin = "anonymous";
      await new Promise((resolve, reject) => {
        baseImg.onload = resolve;
        baseImg.onerror = reject;
        baseImg.src = scorecardConfig.baseImage.url;
      });
      ctx.drawImage(baseImg, 0, 0, scorecardConfig.canvas.width, scorecardConfig.canvas.height);
    } catch (err) {
      console.error("Error loading base image:", err);
      ctx.fillStyle = "#0a0a0a";
      ctx.fillRect(0, 0, scorecardConfig.canvas.width, scorecardConfig.canvas.height);
    }

    if (profileImage) {
      try {
        const img = new Image();
        img.crossOrigin = "anonymous";
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = profileImage;
        });
        ctx.save();
        ctx.beginPath();
        ctx.arc(
          scorecardConfig.profileImage.x,
          scorecardConfig.profileImage.y,
          scorecardConfig.profileImage.radius,
          0,
          2 * Math.PI
        );
        ctx.clip();
        ctx.drawImage(
          img,
          scorecardConfig.profileImage.x - scorecardConfig.profileImage.radius,
          scorecardConfig.profileImage.y - scorecardConfig.profileImage.radius,
          scorecardConfig.profileImage.size,
          scorecardConfig.profileImage.size
        );
        ctx.restore();
        ctx.strokeStyle = scorecardConfig.profileImage.borderColor;
        ctx.lineWidth = scorecardConfig.profileImage.borderWidth;
        ctx.beginPath();
        ctx.arc(
          scorecardConfig.profileImage.x,
          scorecardConfig.profileImage.y,
          scorecardConfig.profileImage.radius,
          0,
          2 * Math.PI
        );
        ctx.stroke();
      } catch (err) {
        console.error("Error loading profile image:", err);
      }
    } else {
      ctx.beginPath();
      ctx.arc(
        scorecardConfig.profileImage.x,
        scorecardConfig.profileImage.y,
        scorecardConfig.profileImage.radius,
        0,
        2 * Math.PI
      );
      ctx.fillStyle = scorecardConfig.profileImage.placeholderColor;
      ctx.fill();
      ctx.strokeStyle = scorecardConfig.profileImage.placeholderBorderColor;
      ctx.lineWidth = scorecardConfig.profileImage.borderWidth;
      ctx.stroke();
      ctx.fillStyle = scorecardConfig.profileImage.placeholderBorderColor;
      ctx.font = scorecardConfig.profileImage.placeholderFont;
      ctx.textAlign = "center";
      ctx.fillText(
        scorecardConfig.profileImage.placeholderText,
        scorecardConfig.profileImage.x,
        scorecardConfig.profileImage.y + 12
      );
    }

    ctx.fillStyle = scorecardConfig.iqScore.color;
    ctx.font = scorecardConfig.iqScore.font;
    ctx.textAlign = "center";
    ctx.fillText(scorecardConfig.iqScore.text(iqScore), scorecardConfig.iqScore.x, scorecardConfig.iqScore.y);

    ctx.fillStyle = scorecardConfig.name.color;
    ctx.font = scorecardConfig.name.font;
    ctx.textAlign = "center";
    ctx.fillText(scorecardConfig.name.text(name), scorecardConfig.name.x, scorecardConfig.name.y);

    return canvas.toDataURL("image/png");
  };

  // Dummy data for dashboard
  const iqScore = 127;
  const chatSummary = {
    chatsUsed: completedChats,
    avgResponseLength: 45,
    attitude: "Curious Explorer",
  };

  const analyticalReasoningChart = generateChartLabels(4, "week");
  const analyticalReasoningData = [70, 75, 78, 82].slice(-analyticalReasoningChart.dataSlice);

  const situationalAwarenessChart = generateChartLabels(4, "month");
  const situationalAwarenessData = [65, 70, 75, 80].slice(-situationalAwarenessChart.dataSlice);

  const subIQMetrics = {
    analyticalReasoning: {
      score: 82,
      description: "Your child shows strong problem-solving skills in logic games.",
      chartData: {
        labels: analyticalReasoningChart.labels,
        datasets: [
          {
            label: "Analytical Reasoning",
            data: analyticalReasoningData,
            backgroundColor: "rgba(0, 229, 255, 0.6)",
            borderColor: "rgba(0, 229, 255, 1)",
            borderWidth: 1,
          },
        ],
      },
    },
    creativeIntelligence: {
      score: 75,
      description: "Creative storytelling and unique ideas detected in Drocto chats.",
      chartData: {
        labels: generateChartLabels(3, "category").labels,
        datasets: [
          {
            label: "Creative Intelligence",
            data: [70, 80, 75],
            backgroundColor: "rgba(0, 229, 255, 0.6)",
            borderColor: "rgba(0, 229, 255, 1)",
            pointBackgroundColor: "rgba(0, 229, 255, 1)",
            pointBorderColor: "#ffffff",
            pointHoverBackgroundColor: "#ffffff",
            pointHoverBorderColor: "rgba(0, 229, 255, 1)",
          },
        ],
      },
    },
    situationalAwareness: {
      score: 80,
      description: "Shows understanding of real-life scenarios and smart decision making.",
      chartData: {
        labels: situationalAwarenessChart.labels,
        datasets: [
          {
            label: "Situational Awareness",
            data: situationalAwarenessData,
            borderColor: "rgba(0, 229, 255, 1)",
            backgroundColor: "rgba(0, 229, 255, 0.2)",
            fill: true,
            tension: 0.4,
          },
        ],
      },
    },
  };

  const challenges = [
    {
      title: "Step 1: Mystery Missions",
      description: "Let your child explore Drocto's mystery missions to enhance critical thinking and problem-solving skills through engaging challenges.",
      price: "₹300/month",
      image: mysteryImage,
    },
    {
      title: "Step 2: AI Timetable",
      description: "Manage your AI timetable to organize learning schedules efficiently, ensuring a balanced approach to education and fun.",
      price: "₹300/month",
      image: timetableImage,
    },
    {
      title: "Step 3: Study Smart",
      description: "Learn the most effective ways to study at home after school, optimizing productivity and retention with smart techniques.",
      price: "₹300/month",
      image: studyImage,
    },
    {
      title: "Step 4: Brain Boosters",
      description: "Engage in daily brain teasers and puzzles to boost cognitive abilities, memory, and logical reasoning in a fun way.",
      price: "₹300/month",
      image: brainImage,
    },
    {
      title: "Step 5: Real-World Scenarios",
      description: "Practice decision-making in simulated real-world scenarios to develop situational awareness and practical life skills.",
      price: "₹300/month",
      image: scenariosImage,
    },
  ];

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
    },
    scales: {
      x: {
        ticks: { color: "#E0E0E0", font: { size: 10 } },
        grid: { display: false },
      },
      y: {
        ticks: { color: "#E0E0E0", font: { size: 10 } },
        grid: { color: "rgba(255, 255, 255, 0.05)" },
        beginAtZero: true,
        max: 100,
      },
    },
  };

  const radarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      r: {
        ticks: { display: false },
        grid: { color: "rgba(255, 255, 255, 0.1)" },
        angleLines: { color: "rgba(255, 255, 255, 0.1)" },
        pointLabels: { color: "#E0E0E0", font: { size: 10 } },
      },
    },
  };

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    cutout: "80%",
  };

  const iqChartData = {
    datasets: [
      {
        data: [iqScore, 200 - iqScore],
        backgroundColor: ["rgba(0, 229, 255, 0.8)", "rgba(255, 255, 255, 0.05)"],
        borderColor: ["rgba(0, 229, 255, 1)", "rgba(255, 255, 255, 0.05)"],
        borderWidth: 2,
      },
    ],
  };

  // Share score function
  const shareScore = async () => {
    const shareText = `My Drocto IQ score is ${iqScore}! Build Critical-Thinking, Street-Smartness, and Real-World Skills here: drocto.in`;
    const shareUrl = "https://drocto.in";

    try {
      const imageDataUrl = await generateScorecardImage(iqScore, userName);

      const response = await fetch(imageDataUrl);
      const blob = await response.blob();
      const file = new File([blob], "drOcto-iq.png", { type: "image/png" });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: "Drocto IQ Score",
          text: shareText,
          url: shareUrl,
          files: [file],
        });
      } else {
        const encodedText = encodeURIComponent(shareText + "\n" + shareUrl);
        const whatsappUrl = `https://wa.me/?text=${encodedText}`;
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          shareUrl
        )}&quote=${encodedText}`;

        const shareWindow = window.open("", "_blank", "width=600,height=400");
        if (shareWindow) {
          shareWindow.document.write(`
            <html>
              <head>
                <title>Share Drocto IQ Score</title>
                <style>
                  body { font-family: Inter, sans-serif; background: #0a0a0a; color: #ffffff; text-align: center; padding: 20px; }
                  h3 { margin-bottom: 20px; }
                  img { max-width: 100%; margin-bottom: 20px; }
                  a { display: inline-block; padding: 10px 20px; margin: 10px; color: #ffffff; text-decoration: none; border-radius: 8px; }
                  .whatsapp { background: #25D366; }
                  .facebook { background: #3b5998; }
                </style>
              </head>
              <body>
                <h3>Share Your Drocto IQ Score</h3>
                <img src="${imageDataUrl}" alt="IQ Scorecard" />
                <a href="${whatsappUrl}" target="_blank" class="whatsapp">Share on WhatsApp</a>
                <a href="${facebookUrl}" target="_blank" class="facebook">Share on Facebook</a>
              </body>
            </html>
          `);
          shareWindow.document.close();
        } else {
          alert(`Sharing is not fully supported. Copy this: ${shareText}\n${shareUrl}`);
        }
      }
    } catch (err) {
      console.error("Error sharing score:", err);
      alert(`Sharing failed. Copy this: ${shareText}\n${shareUrl}`);
    }
  };

  // Render My Score tab content
  const renderMyScore = () => {
    if (completedChats < 35) {
      const chatsRemaining = 35 - completedChats;
      const progressPercentage = Math.round((completedChats / 35) * 100);

      return (
        <div className="relative p-8 rounded-3xl border shadow-2xl overflow-hidden">
          <div className="relative z-10 text-center space-y-6">
            <h3 className="text-3xl font-bold tracking-tight animate-pulse">DroctoIQ Score</h3>
            <p className="text-lg leading-relaxed">
              Chat with Drocto for <span className="font-bold">{chatsRemaining}</span> more{" "}
              {chatsRemaining === 1 ? "day" : "days"} to reveal your IQ score!
            </p>
            <div className="relative w-40 h-40 mx-auto">
              <svg className="w-full h-full" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="rgba(255, 255, 255, 0.1)"
                  strokeWidth="10"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="url(#progressGradient)"
                  strokeWidth="10"
                  strokeDasharray="283"
                  strokeDashoffset={283 - (283 * progressPercentage) / 100}
                  className="transition-all duration-1000 ease-in-out"
                  style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%" }}
                />
                <defs>
                  <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: "#00e5ff", stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: "#3b82f6", stopOpacity: 1 }} />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-3xl font-bold">
                {progressPercentage}%
              </div>
            </div>
            <div className="w-48 h-48 mx-auto">
              <Lottie animationData={octopusAnimation} loop={true} />
            </div>
          </div>
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full animate-particle"
                style={{
                  width: `${Math.random() * 4 + 2}px`,
                  height: `${Math.random() * 4 + 2}px`,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  animationDuration: `${Math.random() * 5 + 5}s`,
                  backgroundColor: "rgba(0, 229, 255, 0.5)",
                }}
              ></div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="p-6 rounded-3xl shadow-2xl animate-fade-in">
          <h3 className="text-2xl font-bold mb-4 tracking-tight">Drocto IQ</h3>
          <div className="relative w-40 h-40 mx-auto">
            <Doughnut data={iqChartData} options={doughnutChartOptions} />
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-4xl font-bold">
              {iqScore}
            </div>
          </div>
          <p className="text-xl font-bold mt-4 text-center">Above Average – Great Progress!</p>
          <p className="text-sm mt-2 text-center">IQ calculated from your child’s AI chat history</p>
        </div>
        <div className="p-6 rounded-3xl shadow-2xl animate-fade-in">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl text-center shadow-lg">
              <p className="text-sm">Chats Completed</p>
              <p className="text-lg font-bold">{chatSummary.chatsUsed}</p>
            </div>
            <div className="p-4 rounded-xl text-center shadow-lg">
              <p className="text-sm">Avg. Response Length</p>
              <p className="text-lg font-bold">{chatSummary.avgResponseLength} words</p>
            </div>
          </div>
          <div className="p-4 rounded-xl text-center shadow-lg mt-4">
            <p className="text-sm">Attitude</p>
            <p className="text-lg font-bold">{chatSummary.attitude}</p>
          </div>
        </div>
        <div className="p-6 rounded-3xl shadow-2xl animate-fade-in">
          <h3 className="text-xl font-bold mb-4 tracking-tight">Sub-IQ Metrics</h3>
          <Swiper modules={[Navigation, Pagination]} spaceBetween={16} slidesPerView={1} pagination={{ clickable: true }} className="pb-10">
            <SwiperSlide>
              <div className="p-6 rounded-xl shadow-lg">
                <h4 className="text-lg font-bold">Analytical Reasoning</h4>
                <p className="text-sm">Score: {subIQMetrics.analyticalReasoning.score}/100</p>
                <div className="h-32 mt-4">
                  <Bar data={subIQMetrics.analyticalReasoning.chartData} options={chartOptions} />
                </div>
                <p className="text-sm mt-4">{subIQMetrics.analyticalReasoning.description}</p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className="p-6 rounded-xl shadow-lg">
                <h4 className="text-lg font-bold">Creative Intelligence</h4>
                <p className="text-sm">Score: {subIQMetrics.creativeIntelligence.score}/100</p>
                <div className="h-32 mt-4">
                  <Radar data={subIQMetrics.creativeIntelligence.chartData} options={radarChartOptions} />
                </div>
                <p className="text-sm mt-4">{subIQMetrics.creativeIntelligence.description}</p>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className="p-6 rounded-2xl shadow-lg">
                <h4 className="text-lg font-bold">Situational Awareness</h4>
                <p className="text-sm">Score: {subIQMetrics.situationalAwareness.score}/100</p>
                <div className="h-32 mt-4">
                  <Line data={subIQMetrics.situationalAwareness.chartData} options={chartOptions} />
                </div>
                <p className="text-sm mt-4">{subIQMetrics.situationalAwareness.description}</p>
              </div>
            </SwiperSlide>
          </Swiper>
        </div>
        <div className="p-6 rounded-3xl shadow-2xl animate-fade-in">
          <h3 className="text-xl font-bold mb-4 tracking-tight">Road to the Best Kid</h3>
          <div className="flex overflow-x-auto space-x-4 pb-4 snap-x snap-mandatory">
            {challenges.map((challenge, index) => (
              <div
                key={index}
                className={`challenge-card p-4 rounded-xl shadow-lg hover:shadow-2xl transition min-w-[250px] cursor-pointer snap-center box-border ${
                  selectedChallenges.includes(challenge.title) ? "selected" : ""
                }`}
                onClick={() => handleChallengeSelect(challenge.title)}
              >
                <p className="text-sm font-semibold mb-2">Step {index + 1}</p>
                <img src={challenge.image} alt={challenge.title} className="w-full h-32 rounded-lg object-cover mb-2" />
                <p className="font-bold text-sm mb-1">{challenge.title}</p>
                <p className="text-xs mb-1">{challenge.description}</p>
                <p className="text-xs text-cyan-400">{challenge.price}</p>
              </div>
            ))}
          </div>
          {selectedChallenges.length > 0 && (
            <button
              onClick={sendWhatsAppMessage}
              className="w-full py-3 mt-4 rounded-lg text-white font-semibold transition shadow-lg"
              style={{ background: "linear-gradient(to right, #00e5ff, #3b82f6)" }}
              aria-label="I'm Interested"
            >
              I'm Interested
            </button>
          )}
        </div>
        <div className="p-6 rounded-3xl shadow-2xl animate-fade-in">
          <button
            onClick={shareScore}
            className="w-full py-3 rounded-lg text-white font-semibold transition shadow-lg"
            style={{ background: "linear-gradient(to right, #00e5ff, #3b82f6)" }}
            aria-label="Share IQ score"
          >
            Share IQ Score
          </button>
        </div>
      </>
    );
  };

  // Render Leaderboard tab content
  const renderLeaderboard = () => (
    <div className="p-6 rounded-3xl shadow-2xl animate-fade-in">
      <RankList userId={userId} userName={userName} onBack={handleBack} />
    </div>
  );

  return (
    <div className="iq-dashboard min-h-screen flex flex-col p-4 sm:p-6" style={{ marginTop: window.innerWidth <= 768 ? "100px" : "120px" }}>
      <div className="w-full max-w-md sm:max-w-lg mx-auto space-y-6">
        {/* Tab Navigation */}
        <div className="flex justify-center gap-4 mb-4">
          <button
            className={`tab-button px-6 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
              activeTab === "myScore"
                ? "bg-neon-cyan text-black shadow-neon-cyan/60"
                : "bg-gray-800/50 text-white hover:bg-gray-700/60"
            }`}
            onClick={() => setActiveTab("myScore")}
            aria-label="View My Score"
          >
            My Score
          </button>
          <button
            className={`tab-button px-6 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
              activeTab === "leaderboard"
                ? "bg-neon-cyan text-black shadow-neon-cyan/60"
                : "bg-gray-800/50 text-white hover:bg-gray-700/60"
            }`}
            onClick={() => setActiveTab("leaderboard")}
            aria-label="View Leaderboard"
          >
            Leaderboard
          </button>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">{activeTab === "myScore" ? renderMyScore() : renderLeaderboard()}</div>
      </div>
      <style>{`
        .iq-dashboard {
          background-color: #0a0a0a;
          color: #ffffff;
        }
        .iq-dashboard h3,
        .iq-dashboard h4,
        .iq-dashboard .font-bold:not(.text-cyan-400) {
          color: #ffffff;
        }
        .iq-dashboard p,
        .iq-dashboard .text-sm:not(.text-cyan-400) {
          color: #d1d5db;
        }
        .iq-dashboard .text-cyan-400,
        .iq-dashboard .font-bold.text-cyan-400 {
          color: #00e5ff;
        }
        .iq-dashboard .p-6,
        .iq-dashboard .p-4 {
          background: rgba(17, 24, 39, 0.5);
          border: 1px solid rgba(55, 65, 81, 0.3);
          backdrop-filter: blur(12px);
        }
        .iq-dashboard .rounded-xl,
        .iq-dashboard .rounded-2xl,
        .iq-dashboard .rounded-3xl {
          border-color: rgba(55, 65, 81, 0.3);
        }
        .iq-dashboard button:not(.tab-button) {
          background: linear-gradient(to right, #00e5ff, #3b82f6);
          color: #ffffff;
        }
        .iq-dashboard button:not(.tab-button):hover {
          background: linear-gradient(to right, #00d4e6, #2563eb);
        }
        .iq-dashboard .swiper-pagination-bullet {
          background: #00e5ff;
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .snap-x {
          scroll-snap-type: x mandatory;
        }
        .snap-center {
          scroll-snap-align: center;
        }
        .box-border {
          box-sizing: border-box;
        }
        .challenge-card {
          border: 2px solid transparent;
          transition: border 0.3s ease;
        }
        .challenge-card.selected {
          border: 2px solid #3b82f6 !important;
        }
        .animate-particle {
          animation: particle-move linear infinite;
          opacity: 0;
        }
        @keyframes particle-move {
          0% { transform: translateY(0); opacity: 0; }
          50% { opacity: 0.7; }
          100% { transform: translateY(-100vh); opacity: 0; }
        }
        /* Tab-specific styling */
        .tab-button.bg-neon-cyan {
          background-color: #00e5ff !important;
          box-shadow: 0 0 10px rgba(0, 229, 255, 0.6) !important;
        }
        .tab-button.bg-gray-800\\/50 {
          background-color: rgba(31, 41, 55, 0.5) !important;
        }
        .tab-button.bg-gray-800\\/50:hover {
          background-color: rgba(55, 65, 81, 0.6) !important;
        }
        /* Override RankList styles */
        .iq-dashboard .min-h-screen.bg-dark-blue {
          background-color: transparent !important;
          margin-top: 0 !important;
          padding: 0 !important;
          min-height: auto !important;
        }
        .iq-dashboard .max-w-md.sm\\:max-w-lg {
          width: 100%;
        }
      `}</style>
    </div>
  );
};

export default DroctoIQDashboard;